import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { useForm } from 'react-hook-form';
import { useNotification } from '../contexts/NotificationContext';
import DeviceManagement from '../components/DeviceManagement';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

interface PasswordChangeForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const UserSettings = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const [passwordChanged, setPasswordChanged] = useState(false);
  
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm<PasswordChangeForm>();

  const watchNewPassword = watch('newPassword');

  // We no longer need to explicitly pass these URLs
  // The QRCodeGenerator component now gets them automatically
  
  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: (data: {currentPassword: string, newPassword: string}) => 
      api.post('/auth/change-password', data),
    onSuccess: () => {
      reset();
      setPasswordChanged(true);
      showNotification({
        type: 'success',
        title: t('settings.passwordChanged', 'Password Changed'),
        message: t('settings.passwordChangedMessage', 'Your password has been successfully updated')
      });
      
      setTimeout(() => {
        setPasswordChanged(false);
      }, 5000);
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: t('common.notifications.error'),
        message: error?.response?.data?.error || t('settings.passwordChangeError', 'Failed to change password')
      });
    }
  });

  const onSubmit = (data: PasswordChangeForm) => {
    changePasswordMutation.mutate({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword
    });
  };

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{t('settings.title', 'User Settings')}</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Profile Info */}
        <Card>
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h2 className="font-medium text-gray-700">{t('settings.profileInfo', 'Profile Information')}</h2>
          </div>
          <CardContent className="p-4">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">{t('settings.name', 'Name')}</h3>
                <p className="mt-1 text-gray-900">{user?.first_name} {user?.last_name}</p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">{t('settings.email', 'Email')}</h3>
                <p className="mt-1 text-gray-900">{user?.email}</p>
              </div>
              
              {user?.position && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500">{t('settings.position', 'Position')}</h3>
                  <p className="mt-1 text-gray-900">{user?.position}</p>
                </div>
              )}
              
              {user?.roles && user.roles.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500">{t('settings.roles', 'Roles')}</h3>
                  <div className="mt-1 space-y-1">
                    {user.roles.map(role => (
                      <span 
                        key={role.id}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-2"
                      >
                        {role.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Change Password */}
        <Card>
          <div className="bg-gray-50 px-4 py-2 border-b">
            <h2 className="font-medium text-gray-700">{t('settings.changePassword', 'Change Password')}</h2>
          </div>
          <CardContent className="p-4">
            {passwordChanged ? (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                {t('settings.passwordChangedSuccess', 'Password successfully changed!')}
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <Input
                  label={t('settings.currentPassword', 'Current Password')}
                  type="password"
                  {...register('currentPassword', {
                    required: t('settings.currentPasswordRequired', 'Current password is required')
                  })}
                  error={errors.currentPassword?.message}
                />
                
                <Input
                  label={t('settings.newPassword', 'New Password')}
                  type="password"
                  {...register('newPassword', {
                    required: t('settings.newPasswordRequired', 'New password is required'),
                    minLength: {
                      value: 8,
                      message: t('settings.passwordMinLength', 'Password must be at least 8 characters')
                    },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/,
                      message: t('settings.passwordPattern', 'Password must include uppercase, lowercase, number and special character')
                    }
                  })}
                  error={errors.newPassword?.message}
                />
                
                <Input
                  label={t('settings.confirmPassword', 'Confirm New Password')}
                  type="password"
                  {...register('confirmPassword', {
                    required: t('settings.confirmPasswordRequired', 'Please confirm your new password'),
                    validate: value => value === watchNewPassword || t('settings.passwordsDontMatch', 'Passwords do not match')
                  })}
                  error={errors.confirmPassword?.message}
                />
                
                <Button
                  type="submit"
                  isLoading={changePasswordMutation.isPending}
                  disabled={changePasswordMutation.isPending}
                >
                  {t('settings.updatePassword', 'Update Password')}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
        
        {/* Mobile Device Management */}
        <DeviceManagement 
          userId={user?.id}
          username={user?.email}
        />
      </div>
    </div>
  );
};

export default UserSettings;